import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HelpTip } from "@/components/HelpTip";

const COUNT_LABELS = {
  test_dealers: "Test dealers",
  users: "Dealer users",
  locations: "Locations",
  projects: "Projects",
  sample_requests: "Sample requests",
  reservations: "Reservations",
  orders: "Orders",
  shipments: "Shipments",
  notifications: "Notifications",
  notification_log: "Notification dedupe rows",
  email_log: "Email-log records",
  dealer_pricing: "Dealer pricing overrides",
  dealer_retail_prices: "Dealer retail prices",
  sample_allowance: "Sample allowance rows",
  inventory_unwind_actions: "Inventory unwind actions",
};

const CONFIRM_TEXT = "DELETE ALL TEST DATA";

export default function AdminTestData() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState("");
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/maintenance/test-data/summary");
      setSummary(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const execute = async () => {
    if (confirm !== CONFIRM_TEXT) return;
    setRunning(true);
    setReport(null);
    try {
      const { data } = await api.post("/admin/maintenance/test-data-cleanup",
        { dry_run: false, confirm });
      setReport(data);
      setConfirm("");
      if (data.verification === "PASS") toast.success("All test data permanently deleted — verification PASS");
      else toast.error("Cleanup finished but verification found remaining records");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" && d?.message ? d.message : formatApiErrorDetail(d));
      if (typeof d === "object" && d?.blocked) setReport({ blocked: d.blocked, verification: "BLOCKED" });
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-warmgrey" /></div>;

  const counts = summary?.counts || {};
  const blocked = report?.blocked || summary?.blocked || [];
  const unflagged = summary?.unflagged_children_of_test_dealers || {};
  const totalRecords = Object.entries(counts)
    .filter(([k]) => k !== "inventory_unwind_actions")
    .reduce((n, [, v]) => n + v, 0);
  const canExecute = confirm === CONFIRM_TEXT && blocked.length === 0 && Object.keys(unflagged).length === 0 && totalRecords > 0;

  return (
    <div data-testid="admin-test-data-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Operations</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Test Data</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
            Everything created under a <span className="badge-status badge-reserved">TEST</span> dealer is flagged end-to-end and listed here.
            Cleanup unwinds inventory effects first, then permanently deletes the records. Real business data is never touched.
          </p>
        </div>
        <HelpTip testid="help-test-data" text="Create a dealer with 'Test / Demo Dealer' ON. Every user, project, sample, reservation, order, shipment, notification and email created from it is automatically flagged as test data. This page removes all of it in one audited, guarded operation — inventory is reconciled before anything is deleted." />
      </div>

      {blocked.length > 0 && (
        <div className="mt-8 rounded-md border border-red-200 bg-red-50 p-5" data-testid="test-data-blocked">
          <p className="flex items-center gap-2 text-sm font-semibold text-red-800"><AlertTriangle className="h-4 w-4" /> Cleanup blocked — resolve manually first</p>
          <ul className="mt-3 space-y-1.5 text-sm text-red-800">
            {blocked.map((b, i) => <li key={i} data-testid={`blocked-item-${i}`}>{b.order_number ? `${b.order_number}: ` : ""}{b.reason}</li>)}
          </ul>
        </div>
      )}
      {Object.keys(unflagged).length > 0 && (
        <div className="mt-8 rounded-md border border-amber-200 bg-amber-50 p-5" data-testid="test-data-unflagged">
          <p className="text-sm font-semibold text-amber-800">Test dealers have unflagged (pre-framework) children — resolve manually before cleanup:</p>
          <p className="mt-1 text-sm text-amber-800">{Object.entries(unflagged).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="test-data-counts">
        {Object.entries(COUNT_LABELS).map(([key, label]) => (
          <div key={key} className="rounded-md border border-border bg-white p-4" data-testid={`test-count-${key}`}>
            <p className="font-display text-2xl font-light text-charcoal">{counts[key] ?? 0}</p>
            <p className="mt-1 text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      {(summary?.dealers?.length > 0) && (
        <div className="mt-8 rounded-md border border-border bg-white p-5" data-testid="test-data-dealers">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Test dealers</p>
          <div className="mt-3 space-y-2">
            {summary.dealers.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`test-dealer-${d.dealer_code}`}>
                <span className="font-mono text-xs text-warmgrey">{d.dealer_code}</span>
                <span className="font-medium text-charcoal">{d.dba || d.legal_name}</span>
                <span className="badge-status badge-reserved">TEST</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(summary?.unwind?.length > 0) && (
        <div className="mt-6 rounded-md border border-border bg-white p-5" data-testid="test-data-unwind">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Inventory reconciliation — performed before any deletion</p>
          <ul className="mt-3 space-y-1.5 text-sm text-warmgrey">
            {summary.unwind.map((u, i) => <li key={i} data-testid={`unwind-item-${i}`}>{u.description}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-8 rounded-md border border-border bg-white p-5" data-testid="test-data-execute">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Delete All Test Data</p>
        <p className="mt-2 text-sm text-warmgrey">
          Permanently deletes the {totalRecords} record(s) above after applying {counts.inventory_unwind_actions ?? 0} inventory unwind action(s).
          Protected objects (canonical catalog, pricing, real containers, real dealers, admin accounts) can never be matched by this operation.
          Type <span className="font-mono text-xs font-semibold text-charcoal">{CONFIRM_TEXT}</span> to enable.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={CONFIRM_TEXT}
            data-testid="test-data-confirm-input" className="h-11 w-full max-w-sm bg-white font-mono text-xs focus-visible:ring-terracotta" />
          <Button onClick={execute} disabled={!canExecute || running} data-testid="test-data-execute-button"
            className="h-11 bg-terracotta text-white transition-all duration-200 hover:bg-terracotta-hover disabled:opacity-50">
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            Delete All Test Data
          </Button>
        </div>
      </div>

      {report && report.verification && report.verification !== "BLOCKED" && (
        <div className={`mt-6 rounded-md border p-5 ${report.verification === "PASS" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`} data-testid="test-data-report">
          <p className={`text-sm font-semibold ${report.verification === "PASS" ? "text-green-800" : "text-red-800"}`} data-testid="test-data-verification">
            Cleanup {report.verification} — {report.unwound?.length ?? 0} inventory unwind action(s),{" "}
            {Object.values(report.deleted || {}).reduce((n, v) => n + v, 0)} records deleted, one audit event written.
          </p>
          {report.unwound?.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-green-900">
              {report.unwound.map((u, i) => <li key={i}>↩ {u}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
